import React, { useState, useEffect } from "react";
import { TextInput } from "@/components/ui/forms/TextInput";
import { Checkbox } from "@/components/ui/forms/Checkbox";
import { Button } from "@/components/ui/buttons/Button";
import styles from "./SettingsSection.module.css";

/** Help text for capacity settings - shown inline below checkboxes */
const HOLIDAYS_HELP =
  "Helligdager (f.eks. 1. mai, 17. mai) telles som arbeidsdager i kapasitetsberegningen.";

/** Generate dynamic weekends help text based on hours per day */
const getWeekendsHelp = (hoursPerDay: number): string => {
  const weekdayHours = 5 * hoursPerDay; // 5 workdays
  const weekendHours = 2 * hoursPerDay; // Saturday + Sunday
  const totalHours = weekdayHours + weekendHours;
  return `Lørdag og søndag legges til i kapasiteten (f.eks. ${weekdayHours}t + ${weekendHours}t = ${totalHours}t per uke).`;
};

export interface OrgSettingsFormProps {
  /** Hours per work day value */
  hoursInDay: number;
  /** Callback when hours changes */
  onHoursChange: (hours: number) => void;
  /** Whether fravaer is disabled */
  disableFravaer: boolean;
  /** Callback when disable fravaer changes */
  onDisableFravaerChange: (disabled: boolean) => void;
  /** Whether to include public holidays in capacity */
  includePublicHolidays: boolean;
  /** Callback when include holidays changes */
  onIncludeHolidaysChange: (include: boolean) => void;
  /** Whether to include weekends in capacity */
  includeWeekends: boolean;
  /** Callback when include weekends changes */
  onIncludeWeekendsChange: (include: boolean) => void;
  /** Whether save operation is in progress */
  isUpdating: boolean;
  /** Callback when save button is clicked */
  onSave: () => void;
  /** Test ID prefix */
  testId?: string;
}

/**
 * OrgSettingsForm - Form for organization settings
 * Handles hours per day, fravaer toggle, and capacity calculation settings
 */
export const OrgSettingsForm: React.FC<OrgSettingsFormProps> = ({
  hoursInDay,
  onHoursChange,
  disableFravaer,
  onDisableFravaerChange,
  includePublicHolidays,
  onIncludeHolidaysChange,
  includeWeekends,
  onIncludeWeekendsChange,
  isUpdating,
  onSave,
  testId = "settings-section",
}) => {
  // Local string state allows user to clear field and type new value
  // Without this, clearing shows "0" immediately due to number parsing
  const [hoursInputValue, setHoursInputValue] = useState(String(hoursInDay));

  // Sync local state when prop changes (e.g., after save or initial load)
  useEffect(() => {
    setHoursInputValue(String(hoursInDay));
  }, [hoursInDay]);

  const handleHoursInputChange = (val: string) => {
    // Allow any input while typing (including empty string)
    setHoursInputValue(val);
  };

  const handleHoursBlur = () => {
    const parsed = parseFloat(hoursInputValue);
    if (Number.isNaN(parsed) || hoursInputValue.trim() === "") {
      // Reset to previous valid value on blur if empty/invalid
      setHoursInputValue(String(hoursInDay));
      return;
    }
    const clamped = Math.min(24, Math.max(1, parsed));
    onHoursChange(clamped);
    setHoursInputValue(String(clamped));
  };

  return (
    <div className={styles.form}>
      {/* Arbeidstid group */}
      <div className={styles.formGroup}>
        <span className={styles.groupLabel}>Arbeidstid</span>

        {/* Timer per dag - top-aligned label */}
        <div className={styles.formField}>
          <label
            htmlFor="hours-in-day"
            className={styles.fieldLabel}
            data-testid={`${testId}-hours-label`}
          >
            Timer per arbeidsdag
          </label>
          <div className={styles.inputWrapper}>
            <TextInput
              id="hours-in-day"
              type="number"
              min={1}
              max={24}
              step={0.5}
              value={hoursInputValue}
              onChange={handleHoursInputChange}
              onBlur={handleHoursBlur}
              disabled={isUpdating}
              className={styles.inputField}
              data-testid={`${testId}-hours-input`}
            />
            <span className={styles.inputSuffix}>timer</span>
          </div>
        </div>

        {/* Deaktiver fravær - checkbox with clickable label */}
        <label
          htmlFor="disable-fravaer"
          className={styles.checkboxField}
          data-testid={`${testId}-fravaer-label`}
        >
          <div className={styles.checkboxWrapper}>
            <Checkbox
              id="disable-fravaer"
              checked={disableFravaer}
              onChange={onDisableFravaerChange}
              disabled={isUpdating}
              data-testid={`${testId}-fravaer-checkbox`}
            />
          </div>
          <span className={styles.checkboxLabel}>Deaktiver fravær</span>
        </label>
      </div>

      {/* Kapasitetsberegning group */}
      <div className={styles.formGroup}>
        <span className={styles.groupLabel}>Kapasitetsberegning</span>

        {/* Inkluder helligdager - checkbox with inline help */}
        <label
          htmlFor="include-holidays"
          className={styles.checkboxField}
          data-testid={`${testId}-holidays-label`}
        >
          <div className={styles.checkboxWrapper}>
            <Checkbox
              id="include-holidays"
              checked={includePublicHolidays}
              onChange={onIncludeHolidaysChange}
              data-testid={`${testId}-holidays-checkbox`}
            />
          </div>
          <div className={styles.checkboxContent}>
            <span className={styles.checkboxLabel}>Inkluder helligdager</span>
            <span className={styles.checkboxHelp}>{HOLIDAYS_HELP}</span>
          </div>
        </label>

        {/* Inkluder helger - checkbox with inline help */}
        <label
          htmlFor="include-weekends"
          className={styles.checkboxField}
          data-testid={`${testId}-weekends-label`}
        >
          <div className={styles.checkboxWrapper}>
            <Checkbox
              id="include-weekends"
              checked={includeWeekends}
              onChange={onIncludeWeekendsChange}
              data-testid={`${testId}-weekends-checkbox`}
            />
          </div>
          <div className={styles.checkboxContent}>
            <span className={styles.checkboxLabel}>Inkluder helger</span>
            <span className={styles.checkboxHelp}>
              {getWeekendsHelp(hoursInDay)}
            </span>
          </div>
        </label>
      </div>

      {/* Save button */}
      <div className={styles.buttonRow}>
        <Button
          onClick={onSave}
          isLoading={isUpdating}
          loadingText="Lagrer..."
          className={styles.saveButton}
          data-testid={`${testId}-save-button`}
        >
          Lagre innstillinger
        </Button>
      </div>
    </div>
  );
};

OrgSettingsForm.displayName = "OrgSettingsForm";
